/**
 * useArloSimilar.js - Resolve Arlo similar-items for a PDP into renderable channels.
 *
 * Sibling of useArloRecommendations, minus the auth gate and the multi-rail
 * shape: one content_id in, a flat list of resolved channels out. content_ids
 * are matched against the channels already loaded via useAppLoad (_id first,
 * then slug — Arlo's docs say slug, live testing shows _ids too), anything else
 * is hydrated one channel at a time, and unresolvable items are dropped. Any
 * failure collapses to { items: [] } — never throws, never a partial error UI.
 */
import { useState, useEffect } from 'react';
import { useAppLoad } from '../contexts/AppLoadContext';
import arloService from '../services/ArloService';
import { buildChannelLookups } from '../utils/channelLookups';
import { hydrateMissingChannels } from '../utils/arloHydration';
import { resolveCustomerId } from '../utils/arloIdentity';

export const useArloSimilar = contentId => {
  const [items, setItems] = useState([]);
  const [attribution, setAttribution] = useState(null);
  const [loading, setLoading] = useState(false);
  const { categories } = useAppLoad();

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (!contentId || !categories || categories.length === 0) {
        setItems([]);
        setAttribution(null);
        return;
      }

      setLoading(true);

      try {
        const { bySlug, byId } = buildChannelLookups(categories);
        // Attribution-only handle (chill-backend#542): the signed-in customer id
        // if there is one, otherwise nothing. The rail shows to everyone either
        // way — similar() is never personalized and is cached on
        // (content_id, country) — a signed-out view simply isn't attributed.
        // Deliberately NOT an anonymous id: see arloIdentity.js.
        const { items: rawItems, attribution: attr } = await arloService.similar(contentId, {
          customerId: resolveCustomerId() || undefined,
        });

        if (cancelled) return;

        const localChannel = id => byId.get(id) || bySlug.get(id);

        const missingIds = (rawItems || [])
          .map(item => item.content_id)
          .filter(id => id && !localChannel(id));
        const hydrated = await hydrateMissingChannels(missingIds);

        if (cancelled) return;

        // The _id of the title this rail is for, so we can drop it if Arlo's
        // vector search returns the query item as its own neighbor.
        const sourceId = localChannel(contentId)?._id;

        // Shallow-copy: the lookup maps hand back the same objects other rails
        // render, and arloRecId must not leak onto them.
        const seen = new Set();
        const resolved = (rawItems || [])
          .map(item => {
            const channel = localChannel(item.content_id) || hydrated.get(item.content_id);
            return channel ? { ...channel, arloRecId: item.rec_id } : null;
          })
          .filter(Boolean)
          .filter(channel => {
            // Never recommend the title back to itself; dedupe when two
            // content_ids resolve to the same channel (first rec_id wins).
            if (channel._id === sourceId || seen.has(channel._id)) return false;
            seen.add(channel._id);
            return true;
          });

        setItems(resolved);
        setAttribution(attr || null);
      } catch (error) {
        console.warn('useArloSimilar: failed to load similar items:', error);
        if (!cancelled) {
          setItems([]);
          setAttribution(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [contentId, categories]);

  return { items, attribution, loading };
};

export default useArloSimilar;
